import { omit } from 'helpers/object';

export const TAX_AMOUNT_NOT_LOADED = -1;
export const TAX_METHOD_LEGACY = 1;
export const TAX_METHOD_TAXSERVICE = 2;

const ADD_CART_ITEM = 'classpass/cart/ADD_CART_ITEM';
const REMOVE_CART_ITEM = 'classpass/cart/REMOVE_CART_ITEM';
const UPDATE_CART_ITEM = 'classpass/cart/UPDATE_CART_ITEM';
const EMPTY_CART = 'classpass/cart/EMPTY_CART';
const SET_TAX_TOTAL = 'classpass/cart/SET_TAX_TOTAL';
const SET_TAX_METHOD = 'classpass/cart/SET_TAX_METHOD';
const SET_CART_CURRENCY = 'classpass/cart/SET_CART_CURRENCY';
const SET_CART_POSTAL_CODE = 'classpass/cart/SET_CART_POSTAL_CODE';

export const addCartItem = (
  itemId,
  name,
  amount,
  {
    description = null,
    hidden = false,
    isFree = false,
    isCredit = false,
    isPostTaxDiscount = false,
    isDebit = !isCredit && !isPostTaxDiscount,
    isTaxable = !isPostTaxDiscount,
    displayAmount = amount,
    content = null,
    sku = '',
    planCode = '',
  } = {}
) => ({
  type: ADD_CART_ITEM,
  data: {
    description,
    itemId,
    name,
    amount: amount * 100,
    isFree,
    isTaxable,
    isCredit,
    isDebit,
    isPostTaxDiscount,
    displayAmount,
    content,
    hidden,
    weight: 0,
    sku,
    planCode,
  },
});
export const removeCartItem = itemId => ({ type: REMOVE_CART_ITEM, data: { itemId } });
export const updateCartItem = (itemId, { amount = Infinity, ...overrides } = {}) => ({
  type: UPDATE_CART_ITEM,
  data: {
    itemId,
    ...overrides,
    amount: amount * 100,
    displayAmount: amount,
  },
});
export const emptyCart = () => ({ type: EMPTY_CART });
export const setTaxMethod = method => ({ type: SET_TAX_METHOD, data: { method } });
export const setTaxOnCart = taxTotal => ({ type: SET_TAX_TOTAL, data: { taxTotal } });
export const setCartCurrency = currencyCode => ({ type: SET_CART_CURRENCY, data: { currencyCode } });
export const setCartPostalCode = postalCode => ({ type: SET_CART_POSTAL_CODE, data: { postalCode } });

export const selectCart = state => state.cart;

const initialState = {
  taxMethod: TAX_METHOD_TAXSERVICE,
  currencyCode: 'USD',
  postalCodeForTaxes: '',
  debitTotal: 0,
  creditTotal: 0,
  subTotal: 0,
  taxTotal: TAX_AMOUNT_NOT_LOADED,
  grandTotal: 0,
  items: [],
};

const selectCredits = item => !!item.isCredit;
const selectDebits = item => !!item.isDebit && !item.isFree;
const selectPostTaxDiscounts = item => !!item.isPostTaxDiscount;
const amountSummer = (memo, item) => memo + item.amount;
const selectLegacyTaxableItems = item => !!item.isTaxable;

const sortByWeight = (a, b) => b.weight - a.weight;

const calculateTotals = state => {
  const debitTotal = state.items.filter(selectDebits).reduce(amountSummer, 0);
  const creditTotal = state.items.filter(selectCredits).reduce(amountSummer, 0);
  const postTaxDiscountTotal = state.items.filter(selectPostTaxDiscounts).reduce(amountSummer, 0);
  const taxAmount = Math.max(state.taxTotal, 0) * 100;
  let subTotal;

  /**
   * Taxes for gift cards (aka post-tax discounts) are handled differently (actually, wrongly)
   * in the legacy code path. So, if we're in legacy mode, we need to recreate the incorrect
   * behaviour. See also: `legacyTaxableTotal`.
   *
   * Example given a $75 plan, $50 gift card:
   *
   * Legacy: $75 - $50 = $25 net, charge tax on the $25 to get grand total
   * Tax service (avalara): Charge tax on $75, then less $50 to get grand total
   */
  let grandTotal;
  if (state.taxMethod === TAX_METHOD_TAXSERVICE) {
    subTotal = debitTotal - creditTotal;
    grandTotal = (subTotal + taxAmount - postTaxDiscountTotal) / 100;
  } else {
    subTotal = debitTotal - creditTotal - postTaxDiscountTotal;
    grandTotal = (subTotal + taxAmount) / 100;
  }

  return {
    ...state,
    debitTotal: debitTotal / 100,
    creditTotal: creditTotal / 100,
    postTaxDiscountTotal: postTaxDiscountTotal / 100,
    subTotal: subTotal / 100,
    grandTotal,
  };
};

export const legacyTaxableTotal = state => {
  const debitItems = state.items.filter(selectDebits);
  const creditItems = state.items.filter(selectCredits);
  const taxableDebits = debitItems.filter(selectLegacyTaxableItems);
  const taxableCredits = creditItems.filter(selectLegacyTaxableItems);
  const taxableDebitTotal = taxableDebits.reduce(amountSummer, 0);
  const taxableCreditTotal = taxableCredits.reduce(amountSummer, 0);
  const postTaxDiscountTotal = state.items.filter(selectPostTaxDiscounts).reduce(amountSummer, 0);
  // to implement incorrect legacy tax behaviour, we subtract post-tax discounts from taxable total
  return taxableDebitTotal - taxableCreditTotal - postTaxDiscountTotal;
};

export default function reducer(state = initialState, action = {}) {
  const { itemId } = action.data || {};

  switch (action.type) {
    case EMPTY_CART:
      return initialState;
    case REMOVE_CART_ITEM:
      return calculateTotals({
        ...state,
        items: state.items.filter(item => item.itemId !== itemId),
      });
    case ADD_CART_ITEM:
      return calculateTotals({
        ...state,
        items: state.items
          .filter(item => item.itemId !== itemId)
          .concat(action.data)
          .sort(sortByWeight),
      });
    case UPDATE_CART_ITEM:
      return calculateTotals({
        ...state,
        items: state.items
          .map(item =>
            item.itemId === itemId
              ? {
                  ...item,
                  ...(Number.isFinite(action.data.amount)
                    ? action.data
                    : omit(['amount', 'displayAmount'], action.data)),
                }
              : item
          )
          .sort(sortByWeight),
      });
    case SET_TAX_TOTAL:
      return calculateTotals({
        ...state,
        taxTotal: action.data.taxTotal,
      });
    case SET_CART_CURRENCY:
      return {
        ...state,
        currencyCode: action.data.currencyCode,
      };
    case SET_CART_POSTAL_CODE:
      return {
        ...state,
        postalCodeForTaxes: action.data.postalCode,
      };
    case SET_TAX_METHOD:
      return {
        ...state,
        taxMethod: action.data.method,
      };
    default:
      return state;
  }
}
